import { type FC, useState } from "react"

import { type PersonalizedDownloadable } from "@forento/core/models/downloadable"
import { toPriceString } from "@forento/core/utilities/currency"

import { usePlatform } from "~/contexts/UserContext"
import trpc from "~/utilities/trpc"

import { AccessButton, containerClasses, Tag, tagsClasses, titleClasses } from "./shared"

type Props = { downloadable: PersonalizedDownloadable; userId: number; reload(): Promise<unknown> }
const Downloadable: FC<Props> = ({ downloadable, userId, reload }) => {
	const hasAssignProductAccess = usePlatform()!.plan.access.assignProducts

	const [isSubmittingAccess, setSubmittingAccess] = useState(false)

	async function handleGiveAccess() {
		setSubmittingAccess(true)
		try {
			await trpc.product.giveManualAccess.mutate({
				product: { type: "downloadable", id: downloadable.id },
				userId,
			})
			await reload()
		} catch (error) {
			console.error(error)
			setSubmittingAccess(false)
		}
		setSubmittingAccess(false)
	}

	async function handleRemoveAccess() {
		setSubmittingAccess(true)
		try {
			await trpc.product.removeManualAccess.mutate({
				product: { type: "downloadable", id: downloadable.id },
				userId,
			})
			await reload()
		} catch (error) {
			console.error(error)
			setSubmittingAccess(false)
		}
		setSubmittingAccess(false)
	}

	return (
		<div className={containerClasses}>
			<h3 className={titleClasses}>{downloadable.name}</h3>
			<div className={tagsClasses}>
				{downloadable.status === "unlocked" ? (
					<>
						{downloadable.unlockedCause === "purchased" &&
							downloadable.order.type === "external" &&
							downloadable.order.createdBy === "manual" && (
								<AccessButton isDanger onClick={handleRemoveAccess} isLoading={isSubmittingAccess}>
									Remove access
								</AccessButton>
							)}
						{downloadable.unlockedCause === "purchased" ? (
							downloadable.order.type === "external" ? (
								downloadable.order.createdBy === "zapier" ? (
									<Tag color="blue">Purchased through Zapier</Tag>
								) : (
									<Tag color="blue">Manual access</Tag>
								)
							) : (
								<Tag color="blue">Purchased</Tag>
							)
						) : downloadable.unlockedCause === "membership" ? (
							<Tag color="blue">Membership</Tag>
						) : null}
					</>
				) : (
					hasAssignProductAccess && (
						<AccessButton onClick={handleGiveAccess} isLoading={isSubmittingAccess}>
							Give access
						</AccessButton>
					)
				)}
				<Tag color="green">
					{downloadable.isManualOnly
						? "Manual only"
						: downloadable.price !== null
							? toPriceString(downloadable.price)
							: downloadable.membershipIds.length > 0
								? "Membership"
								: "Free"}
				</Tag>
			</div>
		</div>
	)
}

export default Downloadable
